/**
=========================================================
* Soft UI Dashboard PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { makeStyles } from "@mui/styles";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";
import boxShadows from "assets/theme/base/boxShadows";

// Soft UI Dashboard PRO React helper functions
import pxToRem from "assets/theme/functions/pxToRem";
import boxShadow from "assets/theme/functions/boxShadow";

const { transparent } = colors;

export const animation = makeStyles(({ palette }) => ({
  "@keyframes prespective": {
    from: {
      opacity: 0,
      transform:
        "perspective(999px) rotateX(-10deg) translateZ(0) translate3d(0, 0, 0)",
    },
    to: {
      opacity: 1,
      transform:
        "perspective(999px) rotateX(0deg) translateZ(0) translate3d(0, 0, 5px)",
    },
  },

  menu: {
    boxShadow: "none !important",
    "& div[class$='-menu']": {
      animationName: "$prespective",
      animationDuration: "250ms",
      animationTimingFunction: "ease",
      animationFillMode: "forwards",
    },

    "& .css-kq55kb-option": {
      opacity: 0.5,
      userSelect: "none",
      cursor: "not-allowed",

      "&:hover, &:focus": {
        color: "currentColor",
        backgroundColor: transparent.main,
      },

      "&:after": {
        content: "''",
      },
    },
  },
}));

export const styles = (
  selectSize,
  selectError,
  selectSuccess,
  hiddenBorder,
  isMultiLine
) => {
  const { dark, white, text, light, inputColors, gradients, transparent } =
    colors;
  const { size, fontWeightRegular } = typography;
  const { borderWidth, borderRadius } = borders;
  const { inputBoxShadow, lg } = boxShadows;

  let borderColorValue;
  let borderColorFocused;
  let boxShadowValue;
  let selectSizeValue;
  let flexWrap = isMultiLine ? "wrap" : "unset";

  if (selectError) {
    borderColorValue = inputColors.error;
    borderColorFocused = inputColors.error;
  } else if (selectSuccess) {
    borderColorValue = inputColors.success;
    borderColorFocused = inputColors.success;
  } else {
    borderColorValue = inputColors.borderColor.main;
    borderColorFocused = inputColors.borderColor.focus;
  }

  if (selectError) {
    boxShadowValue = inputBoxShadow.error;
  } else if (selectSuccess) {
    boxShadowValue = inputBoxShadow.success;
  } else {
    boxShadowValue = boxShadow([0, 0], [0, 2], inputColors.boxShadow, 1);
  }

  if (selectSize === "small") {
    selectSizeValue = pxToRem(32);
  } else if (selectSize === "large") {
    selectSizeValue = pxToRem(48);
  } else {
    selectSizeValue = pxToRem(40);
  }

  return {
    control: (provided, state) => ({
      display: "flex",
      alignItems: "center",
      width: "100%",
      height: !isMultiLine ? selectSizeValue : "auto",
      padding: 0,
      fontSize: selectSize === "small" ? size.xs : size.sm,
      fontWeight: fontWeightRegular,
      backgroundColor: transparent.main,
      backgroundClip: "padding-box",
      border: hiddenBorder
        ? `none`
        : `${borderWidth[1]} solid ${inputColors.borderColor.main}`,
      appearance: "none",
      borderRadius: borderRadius.md,
      transition: "box-shadow 150ms ease, border-color 150ms ease",
      cursor: "pointer",
      borderColor: state.isFocused ? borderColorFocused : borderColorValue,
      boxShadow: state.isFocused ? boxShadowValue : "none",

      "&:hover, &:focus": {
        borderWidth: "1px",
      },
    }),

    valueContainer: () => ({
      display: "flex",
      alignItems: "center",
      width: "100%",
      height: "max-content",
      padding: `${isMultiLine ? pxToRem(8) : 0} ${pxToRem(12)}`,
      color: dark.main,
      flexWrap: flexWrap,
    }),

    placeholder: () => ({
      color: "#495057",
      fontSize: "0.775rem",
      textAlign: "center",
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      marginLeft: 0,
      marginRight: 0,
      opacity: 0.3, // Đảm bảo không bị mờ quá mức
      // Absolutely positioned, so it would otherwise sit on top of the clear /
      // dropdown indicators and swallow their clicks.
      pointerEvents: "none",
    }),

    singleValue: () => ({
      position: "absolute",
      top: selectSize === "small" ? "52%" : "51%",
      transform: "translateY(-50%)",
      color: text.main,
      display: "-webkit-box",
      overflow: "hidden",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: 1,
      // Absolutely positioned and full-width — without this it covers the
      // clear "x" and the click opens the menu instead of clearing.
      pointerEvents: "none",
    }),

    clearIndicator: (provided) => ({
      ...provided,
      padding: pxToRem(4),
      cursor: "pointer",
      color: "#a2abb9",
      zIndex: 2,
      "&:hover": { color: text.main },
    }),

    input: () => ({
      color: dark.main,
      position: "relative",
      margin: 0,
    }),

    indicatorSeparator: () => ({
      display: "none",
    }),

    dropdownIndicator: (provided, state) => ({
      display: "flex",
      padding: pxToRem(12),

      "& svg": {
        fill: text.main,
        width: pxToRem(14),
        height: pxToRem(14),
        strokeWidth: 1,
        stroke: text.main,
        transform: state.selectProps.menuIsOpen
          ? "rotate(180deg)"
          : "rotate(0)",
        transition: "transform 250ms ease",
      },
    }),

    menu: (provided) => ({
      ...provided,
      // minWidth: pxToRem(200),
      boxShadow: lg,
      padding: `${pxToRem(16)} ${pxToRem(8)}`,
      fontSize: size.sm,
      color: text.main,
      textAlign: "left",
      backgroundColor: white.main,
      borderRadius: borderRadius.md,
      // transformOrigin: "50% 0",
      zIndex: 1000,
    }),

    menuList: (provided, state) => ({
      ...provided,
      padding: 0,
      width: "100%",
      maxHeight: state.selectProps.menuListMaxHeight ?? "150px",
    }),

    option: (provided) => ({
      ...provided,
      position: "relative",
      minWidth: "100%",
      width: "100%",
      minHeight: "unset",
      // padding: `${pxToRem(4.8)} ${pxToRem(100)} ${pxToRem(4.8)} ${pxToRem(16)}`,
      borderRadius: borderRadius.md,
      fontSize: size.sm,
      color: text.main,
      cursor: "pointer",
      transition: "background-color 300ms ease, color 300ms ease",
      overflowX: "hidden",

      "&:after": {
        // content: "'Press to select'",
        display: "block",
        fontSize: size.xs,
        position: "absolute",
        right: pxToRem(10),
        top: "52%",
        color: text.main,
        opacity: 0,
        transform: "translateY(-50%)",
        transition: "opacity 300ms ease",
      },

      "&:hover, &:focus": {
        backgroundColor: light.main,
        color: dark.main,

        "&:after": {
          opacity: 0.5,
        },
      },
    }),

    multiValue: (provided) => ({
      ...provided,
      margin: "3px",
      marginRight: pxToRem(4),
      borderRadius: borderRadius.section,
      display: "flex",
      alignItems: "center",
      backgroundColor: gradients.dark.state,
      color: white.main,
      padding: `${pxToRem(2)} 0 ${pxToRem(2)} ${pxToRem(4)}`,

      "& div:first-of-type": {
        color: white.main,
        paddingTop: pxToRem(4),
        paddingBottom: pxToRem(2),
      },

      "& div:last-of-type": {
        paddingTop: pxToRem(1.5),
        opacity: 0.8,

        "&:hover": {
          backgroundColor: transparent.main,
          color: white.main,
        },
      },
    }),
  };
};

export const customStyles = {
  startIconWrapper: {
    display: "flex",
    alignItems: "center",
    margin: "8px 0px 8px 8px",
  },
};
